import React, { useEffect, useState } from "react";
import styles from "./Footer.module.css";
import ThemeToggle from "./ThemeToggle";
import { Link } from "react-router-dom";
import { initializeTikTok, openTikTokPreferences } from "../utils/tiktok";

/**
 * Footer Component
 * 
 * Premium footer with logo, navigation links, social media icons, and copyright.
 * Uses CSS modules with the moodboard design system.
 */
interface FooterProps {
  language?: "en" | "ar";
  tagline?: string;
}

const Footer: React.FC<FooterProps> = ({ language, tagline }) => {
  const currentYear = new Date().getFullYear();
  const isArabic = language === "ar";
  const [measurementEnabled, setMeasurementEnabled] = useState(false);
  useEffect(() => { void initializeTikTok().then(config => setMeasurementEnabled(config.enabled)); }, []);
  const labels = isArabic
    ? {
        logo: "شعار C-Shaped",
        tagline: "أسئلة تدريبية ومساعدة ذكية لدراسة الطب وطب الأسنان",
        product: "المنتج",
        chat: "الدردشة",
        pricing: "الباقات",
        company: "الشركة",
        contact: "تواصل معنا",
        legal: "الشروط والسياسات",
        privacy: "سياسة الخصوصية",
        terms: "شروط الاستخدام",
        follow: "تابعنا",
        rights: "جميع الحقوق محفوظة.",
        theme: "المظهر",
      }
    : {
        logo: "C-Shaped Logo",
        tagline: "Practice questions and AI study support for medicine and dentistry",
        product: "Product",
        chat: "Chat",
        pricing: "Pricing",
        company: "Company",
        contact: "Contact Us",
        legal: "Legal",
        privacy: "Privacy Policy",
        terms: "Terms of Service",
        follow: "Follow Us",
        rights: "All rights reserved.",
        theme: "Theme",
      };

  return (
    <footer
      className={styles.footer}
      dir={language ? (isArabic ? "rtl" : "ltr") : undefined}
      lang={language}
    >
      <div className={styles.footerContainer}>
        <div className={styles.contentWrapper}>
          {/* Logo Section */}
          <div className={styles.logoSection}>
            <svg
              version="1.2"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 1502 451"
              className={styles.logoImage}
              style={{ cursor: 'pointer' }}
              onClick={() => window.location.href = '/'}
              aria-label={labels.logo}
            >
              <title>{labels.logo}</title>
              <style>
                {`.s0 { fill: var(--footer-logo-color, #faf5ef) }`}
              </style>
              <path id="C Shaped" className="s0" aria-label="C Shaped" d="m72.7 337.1q3.4 33 16.5 46.5 13.1 13.1 37.7 10.5 22.7-2.3 41.9-14.8 19.6-12.9 31.5-33.1l9.6 3q-9.6 35.2-40.1 58.9-30.1 23.3-65.5 27-46.2 4.9-69.3-16-22.7-21.4-28.1-72.7-3.5-33 1.1-69.7 4.6-36.7 16.3-66.4 15.3-38.3 40.4-59.8 25.5-22 59.3-25.5 31.8-3.4 50.4 8.8 18.9 11.6 21.6 37.5 1.9 17.9-4.3 28.2-6.2 9.9-19.7 11.3-6.8 0.7-13-1-5.8-2.2-10.8-6.9 6.8-4.4 11.3-15.7 4.4-11.7 3.2-22.9-1.2-11.5-8.2-17.2-7.1-5.7-18.6-4.5-17.5 1.9-32.4 25.1-14.9 22.9-23.7 61.7-6 26.3-7.9 54.7-1.9 27.9 0.8 53zm256.9-161.7q-3.3-30.7 20.9-53.7 24.5-23.1 63.5-27.2 30.2-3.2 48.2 7.4 18.4 10.5 20.8 33.6 1.9 17.9-6.1 30-7.5 12.1-22.3 13.6-5.5 0.6-10.5-0.9-4.6-1.5-8.6-5.5 7.4-5.6 11.2-15.7 4.2-10.1 3.1-20.4-1.4-12.7-9.6-18.7-8.3-6.4-22.7-4.9-17.1 1.8-26 12.4-8.6 10.6-6.8 27.7 1.5 13.9 10 25.9 8.5 11.5 34.5 30.1 34.4 24.2 46.4 43.4 12.1 18.9 14.7 43.5 4.7 45.4-23.9 75.7-28.5 30.4-77.5 35.5-39 4.1-63.5-11.4-24.6-15.5-28-48.1-3-28.7 12.2-48.8 15.2-20.1 39.4-22.6 15.1-1.6 25.4 4.5 10.3 5.8 12.4 18.1-20.7 2.1-32.8 13.5-11.7 11.2-9.8 28.7 1.7 16.7 13.2 26.4 11.5 9.7 29 7.8 20.7-2.2 31.9-17.8 11.2-15.7 8.7-39.9-2-18.7-12.2-35-10.1-16.2-39.4-42.1-26.5-22.9-35.6-36.5-8.7-13.9-10.2-28.6zm282.3 156.9q-0.7-7.2-0.3-18.1 0.9-10.9 4-35 3.2-23.2 3.7-33.3 0.6-10.2-0.1-16.9-1.1-10-5.6-14.7-4.1-4.8-11.3-4.1-9.5 1-17.5 13.5-7.6 12.1-9 24.7l-13.9 132.2-57.3 6 29.3-276.6 58-14.1-10.9 102.5q8.7-12.2 19.7-19 10.9-6.7 24.5-8.2 21-2.2 33.8 8.1 12.7 10.4 15 32.2 1 9.2-0.1 22.2-0.6 12.5-4.3 39-3 20.9-3.7 29.4-0.8 8.5-0.2 14.1 0.9 8.3 5.3 12.3 4.4 3.6 12.4 2.7 9.9-1 16.4-8.5 6.4-7.6 13.3-30l16.7-1.7q-8.8 38.3-25.2 58.5-16 20.2-41 22.8-22.3 2.4-35.9-8.3-13.6-11-15.8-31.7zm219-162.6l0.3 2.8 2.2-21.5 57.3-6.1-14.5 138.3q-0.4 3.7-0.4 6.9-0.2 2.8 0.2 6 0.9 8.8 5.3 12.7 4.4 3.6 12.8 2.7 10.3-1.1 17.7-11.5 7.3-10.8 10.6-29.3l16.7-1.7q-8.9 37.9-27.2 58.7-18.4 20.5-45 23.3-17.1 1.8-28.5-6.7-10.9-8.5-14.4-25.8-10.4 19.6-24.5 30.3-13.8 10.7-30.9 12.5-24.7 2.6-40.7-12.6-15.7-15.3-18.9-45.9-2.4-22.3 1.8-47.3 4.2-24.9 14.1-46.1 12.2-25 30.4-39.4 18.2-14.8 41.3-17.2 15.5-1.6 24.4 3.1 8.9 4.3 9.9 13.8zm-11.2 111.8l9.7-91.9q-0.6-6-5.5-9.9-4.8-4.3-12.4-3.5-21.9 2.3-35.1 40.7-12.9 38.3-9.1 74.1 1.6 15.6 6.8 23.1 5.2 7 16.3 5.9 10.3-1.1 18.9-12.1 8.9-10.9 10.4-26.4zm168.5-100.2l-11.8 113.9q2.1 4.2 6.3 6.2 4.2 1.9 9.8 1.4 25.1-2.7 36.8-36.5 12.1-33.8 7.7-76.4-1.8-16.3-7.4-24.2-5.7-7.8-16-6.7-7.2 0.7-14.1 7.1-7 5.9-11.3 15.2zm-15 144l-10.5 99.2-58.6 20.7 33.5-320.1 57.3-6-3.2 30.5q9.1-9.4 20.1-15 11.1-5.6 24.6-7 23.9-2.5 37.9 12.1 14 14.6 17.3 45.7 6.4 60.8-15.7 103.4-21.7 42.5-67.4 47.3-12.7 1.3-21.9-1.4-8.7-2.7-13.4-9.4zm276.7-183.1q3.5 33-21 60.5-24.5 27.1-61.6 33 0.3 6.8 0.3 10.5 0.3 3.1 0.5 5.5 2 18.3 8.7 25.3 7.2 6.8 23.5 5.1 18.7-1.9 33.3-11.9 15-10.4 32.9-35.7l13.5-1.4q-19.1 39.8-44.3 61-25.1 20.7-57.4 24.1-30.6 3.2-48.2-11-17.2-14.3-20.5-45.7-2.3-22.7 2.1-48.9 4.5-26.2 14.4-47.4 12.2-25.4 31.2-39.4 19.4-14.5 44.9-17.2 21.8-2.3 33.6 6.1 12.1 8 14.1 27.5zm-42.3-12.9q-13.6 1.5-24.9 27.2-11.1 25.3-14.3 59.8 22.9-4 38-25.3 15.4-21.7 12.8-46.4-0.9-8.4-3.7-12.1-2.8-3.7-7.9-3.2zm115.4 103.3q1.6 15.5 6.8 23 5.2 7.1 16.3 6 10-1.1 18.6-11.2 8.5-10.6 10.6-25.3l-0.2-2 9.9-93.1q-1.9-6.3-6.6-9.4-4.4-3.5-11.2-2.8-21.8 2.3-35.1 40.7-12.9 38.3-9.1 74.1zm63.7-121.3l0.1 1.6 9.9-93.5 58-14.2-23.1 219.6q-0.4 3.7-0.5 6.9-0.1 2.9 0.2 6 0.9 8 4.8 11.2 4.3 2.8 13.1 1.8 10.3-1 17.7-11.1 7.4-10 10.8-27.3l16.7-1.7q-8.6 36.7-26.6 56.7-17.6 19.9-45.8 22.9-16.4 1.7-27.6-5.9-10.9-8.2-15-24.2-10 19.5-23.8 30.2-13.8 10.7-31.3 12.6-24.6 2.6-40.7-12.6-15.7-15.3-18.9-45.9-2.3-22.3 1.9-47.3 4.2-24.9 14-46.1 12.3-25 30.5-39.4 18.1-14.8 41.2-17.2 15.5-1.6 24.5 3.1 8.9 4.3 9.9 13.8z" />
            </svg>
            <p className={styles.tagline}>
              {tagline || labels.tagline}
            </p>
          </div>

          {/* Navigation Links Section */}
          <div className={styles.navigationSection}>
            {/* Product Column */}
            <div className={styles.navigationColumn}>
              <h4 className={styles.columnTitle}>{labels.product}</h4>
              <ul className={styles.linksList}>
                <li className={styles.linkItem}>
                  <a href="/chat" className={styles.navLink}>
                    {labels.chat}
                  </a>
                </li>
                <li className={styles.linkItem}>
                  <a href="/pricing" className={styles.navLink}>
                    {labels.pricing}
                  </a>
                </li>
              </ul>
            </div>

            {/* Company Column */}
            <div className={styles.navigationColumn}>
              <h4 className={styles.columnTitle}>{labels.company}</h4>
              <ul className={styles.linksList}>
                <li className={styles.linkItem}>
                  <Link to="/contact-us" className={styles.navLink}>
                    {labels.contact}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal Column */}
            <div className={styles.navigationColumn}>
              <h4 className={styles.columnTitle}>{labels.legal}</h4>
              <ul className={styles.linksList}>
                <li className={styles.linkItem}>
                  <a href="/privacy-policy" className={styles.navLink}>
                    {labels.privacy}
                  </a>
                </li>
                <li className={styles.linkItem}>
                  <a href="/terms" className={styles.navLink}>
                    {labels.terms}
                  </a>
                </li>
                {measurementEnabled && <li className={styles.linkItem}>
                  <button type="button" className={styles.navLink} onClick={openTikTokPreferences}
                    style={{ background: "none", border: 0, padding: 0, font: "inherit", cursor: "pointer" }}>
                    {isArabic ? "إعدادات ملفات تعريف الارتباط" : "Cookie settings"}
                  </button>
                </li>}
              </ul>
            </div>
          </div>
        </div>

        {/* Social Media Section */}
        <div className={styles.socialContainer}>
          <div className={styles.socialSection}>
            <h4 className={styles.socialTitle}>{labels.follow}</h4>
            <div className={styles.socialIcons}>
              {/* LinkedIn */}
              <a
                href="https://www.linkedin.com/company/c-shaped/"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialIcon}
                aria-label="LinkedIn"
              >
                <svg viewBox="0 0 24 24" fill="white">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </a>

              {/* X (Twitter) */}
              <a
                href="https://x.com/C_Shaped"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialIcon}
                aria-label="X (Twitter)"
              >
                <svg viewBox="0 0 24 24" fill="white">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>

              {/* WhatsApp */}
              <a
                href="https://wa.me/966558096476"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialIcon}
                aria-label="WhatsApp"
              >
                <svg viewBox="0 0 24 24" fill="white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className={styles.bottomBar}>
        <p className={styles.copyright}>
          © {currentYear} C-Shaped. {labels.rights}
        </p>
        <div className={styles.themeControl}>
          <span className={styles.themeLabel}>{labels.theme}</span>
          <ThemeToggle className={styles.footerThemeToggle} language={language} />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
